import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Home,
  FolderIcon,
  Wallet,
  Clock,
  DollarSign,
  Building2,
  Users,
  LogOut,
  Send,
  FileText,
  BadgePoundSterling,
  HandIcon,
  HandCoins,
  FileStack,
  LayoutDashboard,
  ReceiptText,
  Calendar,
  User,
  Settings,
  HelpCircle,
  Receipt,
  Info,
} from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/hooks/useAuth";
import {
  isFullUser,
  isNannyUser,
  isSuperUser,
  isContractorUser,
  isClientUser,
} from "@/types/user";
import { useTour } from "@/contexts/TourContext";
import { MenuItem } from "@/types/menu";
import { MenuListItem } from "./MenuItem";
import { MenuSection } from "./MenuSection";
import Logo from "@/components/Logo";
import { EndpointKeys } from "@/config/api";
import ApiService from "@/services/api";
import { SetupChecklistResponse } from "@/types/checklist";
import { GetContractorEntityResponse } from "@/types/contractor-entity";

const MenuBar: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout, isLoading } = useAuth();
  const { isTourAvailable, startTour } = useTour();
  const [checklistData, setChecklistData] =
    useState<SetupChecklistResponse | null>(null);
  const [isLoadingChecklist, setIsLoadingChecklist] = useState(true);
  const [hasContractorEntity, setHasContractorEntity] = useState<boolean>(true);
  const [isLoadingEntity, setIsLoadingEntity] = useState(true);
  const [incompleteCount, setIncompleteCount] = useState<number>(0);
  const [isLoadingIncompleteCount, setIsLoadingIncompleteCount] = useState(true);

  useEffect(() => {
    const fetchChecklistData = async () => {
      if (user?.inImplementation && !isContractorUser(user)) {
        try {
          const response = await ApiService.request<SetupChecklistResponse>(
            EndpointKeys.GET_EMPLOYER_CHECKLIST,
          );
          setChecklistData(response);
        } catch (error) {
          console.error("Error fetching checklist data:", error);
        } finally {
          setIsLoadingChecklist(false);
        }
      } else {
        setIsLoadingChecklist(false);
      }
    };

    fetchChecklistData();
  }, [user]);

  useEffect(() => {
    const fetchContractorEntityData = async () => {
      if (isContractorUser(user)) {
        try {
          const response =
            await ApiService.request<GetContractorEntityResponse>(
              EndpointKeys.GET_CONTRACTOR_ENTITY,
            );
          setHasContractorEntity(response.hasEntity);
        } catch (error) {
          console.error("Error fetching contractor entity data:", error);
          setHasContractorEntity(false);
        } finally {
          setIsLoadingEntity(false);
        }
      } else {
        setIsLoadingEntity(false);
      }
    };

    fetchContractorEntityData();
  }, [user]);

  useEffect(() => {
    const fetchIncompleteCount = async () => {
      // Only fetch for employers and super users, not for nannies or contractors
      if (user && (isFullUser(user) || isSuperUser(user))) {
        try {
          const response = await ApiService.request<{ incompleteCount: number }>(
            EndpointKeys.GET_ONBOARDING_INCOMPLETE_COUNT,
          );
          setIncompleteCount(response.incompleteCount);
        } catch (error) {
          console.error("Error fetching incomplete count:", error);
          setIncompleteCount(0);
        } finally {
          setIsLoadingIncompleteCount(false);
        }
      } else {
        setIsLoadingIncompleteCount(false);
      }
    };

    fetchIncompleteCount();
  }, [user]);

  useEffect(() => {
    if (!user && !isLoading) {
      navigate("/login");
    }
  }, [user, isLoading, navigate]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  if (isLoading || isLoadingChecklist || isLoadingEntity || isLoadingIncompleteCount) {
    return (
      <div className="fixed inset-0 w-64 bg-background border-r flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Show all menu items if either:
  // 1. User is not in implementation
  // 2. User is in implementation but has completed all checklist items
  // 3. User is a contractor (contractors should always have full menu access)
  const shouldShowFullMenu =
    isContractorUser(user) ||
    !user?.inImplementation ||
    (checklistData &&
      checklistData.totalCompleted === checklistData.totalItems);

  // Menu sections for organized navigation
  const getMenuSections = () => {
    if (!shouldShowFullMenu) {
      return {
        sections: [],
        standaloneItems: [
          { to: "/dashboard", icon: <Home className="h-4 w-4" />, text: "Home" },
          { to: "/profile", icon: <User className="h-4 w-4" />, text: "Profile" },
        ]
      };
    }

    if (isNannyUser(user)) {
      return {
        sections: [
          {
            title: "Employment",
            items: [
              {
                to: `/employee/${user.employerEmployeeId}`,
                icon: <Users className="h-4 w-4" />,
                text: "Employment Information",
                class: "employment-information",
                "data-testid": "nanny-menu-employment-info",
              },
              {
                to: "/nanny/contract",
                icon: <FileStack className="h-4 w-4" />,
                text: "Employment Contracts",
                class: "contracts-information",
                "data-testid": "nanny-menu-contracts",
              },
            ],
            defaultExpanded: false,
          },
          {
            title: "Pay & Benefits",
            items: [
              {
                to: "/nanny/payslips",
                icon: <BadgePoundSterling className="h-4 w-4" />,
                text: "Payslips",
                class: "payslip-information",
                "data-testid": "nanny-menu-payslips",
              },
              {
                to: "/nanny/pensions",
                icon: <Wallet className="h-4 w-4" />,
                text: "Pension",
                class: "pension-actions",
                "data-testid": "nanny-menu-pension",
              },
            ],
            defaultExpanded: false,
          },
          {
            title: "Submissions & Filings",
            items: [
              {
                to: "/nanny/submissions",
                icon: <FileText className="h-4 w-4" />,
                text: "My Submissions",
                class: "submissions-information",
                "data-testid": "nanny-menu-submissions",
              },
              {
                to: "/filings",
                icon: <FolderIcon className="h-4 w-4" />,
                text: "My Filings",
                class: "filings-information",
                "data-testid": "nanny-menu-filings",
              },
            ],
            defaultExpanded: false,
          },
          {
            title: "Additional",
            items: [
              {
                to: "/nanny/time-off",
                icon: <Clock className="h-4 w-4" />,
                text: "Time Off Management",
                class: "pto-information",
                "data-testid": "nanny-menu-time-off",
              },
              {
                to: "/referrals",
                icon: <HandIcon className="h-4 w-4" />,
                text: "Referrals",
                class: "referral-information",
                "data-testid": "nanny-menu-referrals",
              },
            ],
            defaultExpanded: false,
          },
        ],
        standaloneItems: [
          {
            to: "/dashboard",
            icon: <Home className="h-4 w-4" />,
            text: "Home",
            "data-testid": "nanny-menu-home",
          },
          {
            to: "/profile",
            icon: <User className="h-4 w-4" />,
            text: "Profile",
            class: "profile-settings",
            "data-testid": "nanny-menu-profile",
          },
        ]
      };
    }

    if (isClientUser(user)) {
      return {
        sections: [
          {
            title: "Client Services",
            items: [
              {
                to: "/client/contracts",
                icon: <FileText className="h-4 w-4" />,
                text: "Contracts",
              },
              {
                to: "/client/expenses",
                icon: <Receipt className="h-4 w-4" />,
                text: "Expenses",
              },
            ],
            defaultExpanded: false,
          },
        ],
        standaloneItems: [
          {
            to: "/dashboard",
            icon: <Home className="h-4 w-4" />,
            text: "Dashboard",
          },
          {
            to: "/profile",
            icon: <User className="h-4 w-4" />,
            text: "Profile",
          },
        ]
      };
    }

    if (isContractorUser(user)) {
      return {
        sections: [
          {
            title: "Business Management",
            items: [
              {
                to: "/contractor/profile",
                icon: <Building2 className="h-4 w-4" />,
                text: "Business Details",
              },
              {
                to: "/contractor/settings",
                icon: <Wallet className="h-4 w-4" />,
                text: "Payment Settings",
                disabled: !hasContractorEntity,
              },
            ],
            defaultExpanded: false,
          },
          {
            title: "Client & Finance",
            items: [
              {
                to: "/contractor/contracts",
                icon: <FileText className="h-4 w-4" />,
                text: "Clients",
                disabled: !hasContractorEntity,
              },
              {
                to: "/contractor/invoices",
                icon: <ReceiptText className="h-4 w-4" />,
                text: "Invoices",
                disabled: !hasContractorEntity,
              },
              {
                to: "/contractor/expenses",
                icon: <Receipt className="h-4 w-4" />,
                text: "Expenses",
                disabled: !hasContractorEntity,
              },
            ],
            defaultExpanded: false,
          },
        ],
        standaloneItems: [
          {
            to: "/dashboard",
            icon: <Home className="h-4 w-4" />,
            text: "Dashboard",
          },
          {
            to: "/profile",
            icon: <User className="h-4 w-4" />,
            text: "Profile",
          },
        ]
      };
    }

    if (isSuperUser(user)) {
      return {
        sections: [
          {
            title: "Operations",
            items: [
              {
                to: "/operations/dashboard",
                icon: <LayoutDashboard className="h-4 w-4" />,
                text: "Operations Dashboard",
                superUserOnly: true,
                "data-testid": "superuser-menu-operations",
              },
              {
                to: "/ops/features",
                icon: <LayoutDashboard className="h-4 w-4" />,
                text: "Feature Dashboard",
                superUserOnly: true,
                "data-testid": "superuser-menu-features",
              },
              {
                to: "/operations/contractors",
                icon: <Users className="h-4 w-4" />,
                text: "Contractor Dashboard",
                superUserOnly: true,
                "data-testid": "superuser-menu-contractors",
              },
            ],
            defaultExpanded: true,
          },
          {
            title: "Setup & Management",
            items: [
              {
                to: "/parent-info",
                icon: <Users className="h-4 w-4" />,
                text: "Parent Information",
                class: "parent-information",
                "data-testid": "superuser-menu-parent-info",
              },
              {
                to: "/employer",
                icon: <Building2 className="h-4 w-4" />,
                text: "Employer Management",
                class: "employer-management",
                "data-testid": "superuser-menu-employer",
              },
              {
                to: "/employees",
                icon: <Users className="h-4 w-4" />,
                text: "Employee Management",
                "data-testid": "superuser-menu-employees",
              },
            ],
            defaultExpanded: false,
          },
          {
            title: "Payroll & Benefits",
            items: [
              {
                to: "/pensions",
                icon: <Wallet className="h-4 w-4" />,
                text: "Pensions",
                class: "pension-management",
                "data-testid": "superuser-menu-pensions",
              },
              {
                to: "/earnings",
                icon: <BadgePoundSterling className="h-4 w-4" />,
                text: "Earnings",
                class: "earnings-management",
                "data-testid": "superuser-menu-earnings",
              },
              {
                to: "/employee-submissions",
                icon: <Send className="h-4 w-4" />,
                text: "Payrun Approvals",
                class: "submission-management",
                "data-testid": "superuser-menu-submissions",
              },
            ],
            defaultExpanded: false,
          },
          {
            title: "Compliance & Filings",
            items: [
              {
                to: "/filings",
                icon: <FolderIcon className="h-4 w-4" />,
                text: "HMRC Filings",
                "data-testid": "superuser-menu-filings",
              },
              {
                to: "/superuser/eps-management",
                icon: <Receipt className="h-4 w-4" />,
                text: "EPS Management",
                superUserOnly: true,
                "data-testid": "superuser-menu-eps",
              },
            ],
            defaultExpanded: false,
          },
          {
            title: "Additional Services",
            items: [
              {
                to: "/time-off",
                icon: <Clock className="h-4 w-4" />,
                text: "Time Off Management",
                class: "pto-management",
                "data-testid": "superuser-menu-time-off",
              },
              {
                to: "/contracts",
                icon: <FileStack className="h-4 w-4" />,
                text: "Employment Contracts",
                class: "contract-management",
                "data-testid": "superuser-menu-contracts",
              },
              {
                to: "/referrals",
                icon: <HandCoins className="h-4 w-4" />,
                text: "Referrals",
                "data-testid": "superuser-menu-referrals",
              },
            ],
            defaultExpanded: false,
          },
        ],
        standaloneItems: [
          {
            to: "/dashboard",
            icon: <Home className="h-4 w-4" />,
            text: "Home",
            "data-testid": "superuser-menu-home",
          },
          {
            to: "/profile",
            icon: <User className="h-4 w-4" />,
            text: "Profile",
            class: "profile-settings",
            "data-testid": "superuser-menu-profile",
          },
        ]
      };
    }

    if (isFullUser(user)) {
      return {
        sections: [
          {
            title: "Setup & Management",
            items: [
              {
                to: "/parent-info",
                icon: <Users className="h-4 w-4" />,
                text: "Parent Information",
                class: "parent-information",
                "data-testid": "employer-menu-parent-info",
              },
              {
                to: "/employer",
                icon: <Building2 className="h-4 w-4" />,
                text: "Employer Management",
                class: "employer-management",
                "data-testid": "employer-menu-employer",
              },
              {
                to: "/employees",
                icon: <Users className="h-4 w-4" />,
                text: "Employee Management",
                "data-testid": "employer-menu-employees",
              },
            ],
            defaultExpanded: false,
          },
          {
            title: "Payroll & Benefits",
            items: [
              {
                to: "/pensions",
                icon: <Wallet className="h-4 w-4" />,
                text: "Pensions",
                class: "pension-management",
                "data-testid": "employer-menu-pensions",
              },
              {
                to: "/earnings",
                icon: <BadgePoundSterling className="h-4 w-4" />,
                text: "Earnings",
                class: "earnings-management",
                "data-testid": "employer-menu-earnings",
              },
              {
                to: "/employee-submissions",
                icon: <Send className="h-4 w-4" />,
                text: "Payrun Approvals",
                class: "submission-management",
                "data-testid": "employer-menu-submissions",
              },
            ],
            defaultExpanded: false,
          },
          {
            title: "Compliance & Filings",
            items: [
              {
                to: "/filings",
                icon: <FolderIcon className="h-4 w-4" />,
                text: "HMRC Filings",
                "data-testid": "employer-menu-filings",
              },
            ],
            defaultExpanded: false,
          },
          {
            title: "Additional Services",
            items: [
              {
                to: "/time-off",
                icon: <Clock className="h-4 w-4" />,
                text: "Time Off Management",
                class: "pto-management",
                "data-testid": "employer-menu-time-off",
              },
              {
                to: "/contracts",
                icon: <FileStack className="h-4 w-4" />,
                text: "Employment Contracts",
                class: "contract-management",
                "data-testid": "employer-menu-contracts",
              },
              {
                to: "/referrals",
                icon: <HandCoins className="h-4 w-4" />,
                text: "Referrals",
                "data-testid": "employer-menu-referrals",
              },
            ],
            defaultExpanded: false,
          },
        ],
        standaloneItems: [
          {
            to: "/dashboard",
            icon: <Home className="h-4 w-4" />,
            text: "Home",
            "data-testid": "employer-menu-home",
          },
          {
            to: "/profile",
            icon: <User className="h-4 w-4" />,
            text: "Profile",
            class: "profile-settings",
            "data-testid": "employer-menu-profile",
          },
        ]
      };
    }

    // Fallback for other user types
    return {
      sections: [],
      standaloneItems: [
        {
          to: "/dashboard",
          icon: <Home className="h-4 w-4" />,
          text: "Home",
        },
        {
          to: "/referrals",
          icon: <HandIcon className="h-4 w-4" />,
          text: "Referrals",
        },
        {
          to: "/profile",
          icon: <User className="h-4 w-4" />,
          text: "Profile",
        },
      ]
    };
  };

  const { sections, standaloneItems } = getMenuSections();

  return (
    <div className="fixed inset-y-0 left-0 w-64 bg-background border-r flex flex-col h-screen">
      <div className="p-4">
        <Logo />
      </div>

      <Separator />

      <div className="min-h-0 flex-1 flex flex-col justify-between">
        <ScrollArea className="flex-1 py-2">
          <div className="px-2 space-y-2">
            {/* Render standalone items first */}
            {standaloneItems.map((item, index) => {
              // Add IDs to specific menu items for the tour to target
              const itemProps = { ...item } as MenuItem & { badge?: React.ReactNode };

              if (item.text === "Employee Management") {
                itemProps.id = "employee-section";
              } else if (
                item.text === "HMRC Filings" ||
                item.text === "My Filings"
              ) {
                itemProps.id = "tax-section";
              }

              // Add badge to Pensions menu item if there are incomplete items
              if (item.text === "Pensions" && incompleteCount > 0 && (isFullUser(user) || isSuperUser(user))) {
                itemProps.badge = (
                  <Badge variant="destructive" className="ml-auto">
                    {incompleteCount}
                  </Badge>
                );
              }

              return (
                <MenuListItem
                  className={`${(item as any).class ? (item as any).class : ""}`}
                  key={`standalone-${index}`}
                  {...itemProps}
                />
              );
            })}

            {/* Render sections */}
            {sections.map((section, sectionIndex) => {
              // Check if any items in this section have badges
              const sectionBadge = section.items.some(item => 
                item.text === "Pensions" && incompleteCount > 0 && (isFullUser(user) || isSuperUser(user))
              ) ? (
                <Badge variant="destructive" className="ml-auto">
                  {incompleteCount}
                </Badge>
              ) : undefined;

              // Add IDs and badges to section items
              const sectionItems = section.items.map(item => {
                const itemProps = { ...item } as MenuItem & { badge?: React.ReactNode };

                if (item.text === "Employee Management") {
                  itemProps.id = "employee-section";
                } else if (
                  item.text === "HMRC Filings" ||
                  item.text === "My Filings"
                ) {
                  itemProps.id = "tax-section";
                }

                // Add badge to Pensions menu item if there are incomplete items
                if (item.text === "Pensions" && incompleteCount > 0 && (isFullUser(user) || isSuperUser(user))) {
                  itemProps.badge = (
                    <Badge variant="destructive" className="ml-auto">
                      {incompleteCount}
                    </Badge>
                  );
                }

                return itemProps;
              });

              return (
                <MenuSection
                  key={`section-${sectionIndex}`}
                  title={section.title}
                  items={sectionItems}
                  defaultExpanded={section.defaultExpanded}
                  badge={sectionBadge}
                />
              );
            })}
          </div>
        </ScrollArea>

        <div className="shrink-0">
          <Separator />
          <div className="p-4">
            {/* Start Tour Button - only show if tour is available and for supported user types */}
            {isTourAvailable && user && !isContractorUser(user) && !isClientUser(user) && (
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-start mb-2"
                onClick={() => startTour()}
                data-testid="start-tour-button"
              >
                <Info className="h-4 w-4 mr-3" />
                Start Tour
              </Button>
            )}
            
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start"
              onClick={handleLogout}
              data-testid="logout-button"
            >
              <LogOut className="h-4 w-4 mr-3" />
              Logout
            </Button>

            {user && (
              <div className="mt-4 pt-4 border-t">
                <div className="flex items-center space-x-3">
                  <Avatar className="h-9 w-9">
                    <AvatarImage
                      src="/placeholder-user.jpg"
                      alt="User avatar"
                    />
                    <AvatarFallback>
                      {user.email.substring(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{user.email}</p>
                    <p className="text-xs text-muted-foreground">
                      {isNannyUser(user)
                        ? "Nanny"
                        : isContractorUser(user)
                          ? "Contractor"
                          : "Employer"}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MenuBar;
